'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import logger from '@/utils/client-logger';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import type {
  BlogArticleData,
  BlogArticleItem,
  BlogArticleProps,
  BlogArticleTrackingParams,
  BlogArticleValidExposureParams,
} from './types';

const DEFAULT_SECTION_BACKGROUND = '#f2f3f5';
const DEFAULT_HEADING_COLOR = '#1a1c1e';
const DEFAULT_HEADING_SUBTITLE_COLOR = '#626668';
const DEFAULT_TITLE_COLOR = '#282828';
const DEFAULT_SECONDARY_COLOR = '#6c6c6c';

/**
 * 配镜流程坑位（choose_frames / add_prescription / customize_lenses / after_sales）
 * 按卡片标题语义匹配：截图证据显示四张卡片标题为 1. Find Your Frames /
 * 2. Add Your Prescription / 3. Customize Your Lenses / 4. Enjoy Your New Glasses；
 * 标题匹配可避免 blogArticle 楼层复用到其他内容时误发编码。
 * 模块编码（C 位）改为按楼块数据动态拼接，坑位名保持不变。
 */
const SHOPPING_GUIDE_SPM_RULES: Array<{ pattern: RegExp; position: string }> = [
  { pattern: /find\s+your\s+frames/i, position: 'choose_frames' },
  { pattern: /add\s+your\s+prescription/i, position: 'add_prescription' },
  { pattern: /customize\s+your\s+lenses/i, position: 'customize_lenses' },
  { pattern: /enjoy\s+your\s+new\s+glasses/i, position: 'after_sales' },
];

const getShoppingGuideSpmPosition = (title?: string): string | undefined => {
  if (!title) return undefined;
  const plainTitle = title.replace(/<[^>]*>/g, ' ');
  return SHOPPING_GUIDE_SPM_RULES.find((rule) => rule.pattern.test(plainTitle))?.position;
};

function hasVisibleContent(item: BlogArticleItem) {
  return Boolean(item.jump_image || item.title || item.des || item.btn_text);
}

function getPlainText(value?: string) {
  return value?.replace(/<[^>]*>/g, '').trim() || '';
}

function useTrackingParams(
  item: BlogArticleItem,
  index: number,
  indexData: BlogArticleData,
  moduleIndex: number,
): BlogArticleTrackingParams {
  return useMemo(() => {
    const jumpData = item.jump_info || item;

    return {
      module_index: moduleIndex,
      module_name: indexData.component_id,
      module_id: String(indexData.blocks_advances_id || ''),
      module_title: indexData.layout_title || '',
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: String(jumpData.jump_type || ''),
      mkt_jump_content: String(jumpData.Jump_target || ''),
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(jumpData.sku?.shop_sku || ''),
      business_sku: String(jumpData.sku?.business_sku || ''),
      product_sku: String(jumpData.sku?.sku || ''),
      product_id: String(jumpData.sku?.id || ''),
      product_name: String(jumpData.sku?.short_name || ''),
      group_id: String(jumpData.product_group?.id || ''),
      group_name: String(jumpData.product_group?.name || ''),
    };
  }, [index, indexData, item, moduleIndex]);
}

function buildValidExposureParams(
  item: BlogArticleItem,
  index: number,
  indexData: BlogArticleData,
  moduleIndex: number,
): BlogArticleValidExposureParams {
  const jumpData = item.jump_info || item;

  return {
    module_index: moduleIndex,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: indexData.layout_title || '',
    module_name: indexData.component_id,
    module_tab_name: '',
    mkt_index: index,
    mkt_index_sub: '',
    mkt_jump_type: String(jumpData.jump_type ?? ''),
    mkt_jump_content: String(jumpData.Jump_target ?? ''),
  };
}

function useTrackingHandlers({
  trackingParams,
  validExposureParams,
  clickSpmCnt,
  exposureSpmCnt,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<BlogArticleProps, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: BlogArticleTrackingParams;
  validExposureParams: BlogArticleValidExposureParams;
  clickSpmCnt?: string;
  exposureSpmCnt?: string;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC blog article exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      // 坑位点击上报完整 spm_cnt；浅拷贝避免污染曝光共用对象。
      onTrack(clickSpmCnt ? withSpmCnt(validExposureParams, clickSpmCnt) : validExposureParams);
    } catch (error) {
      logger.error('2026 PC blog article click tracking error:', error);
    }
  }, [clickSpmCnt, onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () =>
      onValidExpTrack?.(
        exposureSpmCnt ? withSpmCnt(validExposureParams, exposureSpmCnt) : validExposureParams,
      ),
  });

  return {
    cardRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

interface BlogArticleCardProps
  extends Pick<BlogArticleProps, 'moduleIndex' | 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> {
  index: number;
  indexData: BlogArticleData;
  item: BlogArticleItem;
}

function BlogArticleCard({
  item,
  index,
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BlogArticleCardProps) {
  const trackingParams = useTrackingParams(item, index, indexData, moduleIndex);
  const validExposureParams = buildValidExposureParams(item, index, indexData, moduleIndex);
  // 配镜流程坑位按卡片标题语义匹配；规则外卡片（普通文章等）为 undefined，保持原上报。
  // 模块编码（C 位）按楼块数据动态拼接。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_pc');
  const shoppingGuideSpmPosition = getShoppingGuideSpmPosition(item.title);
  // 坑位 D 位编码：点击与有效曝光共用同一坑位；模块码或坑位名缺失（表外数据）时不输出。
  const shoppingGuideSpmCnt =
    spmModule && shoppingGuideSpmPosition ? `${spmModule}.${shoppingGuideSpmPosition}` : undefined;
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报 spm_cnt 同值，表外静默不打标。
  const shoppingGuideDomSpm = useSpmDomAttr(shoppingGuideSpmCnt);
  const { cardRef, handleClick } = useTrackingHandlers({
    trackingParams,
    validExposureParams,
    clickSpmCnt: shoppingGuideSpmCnt,
    // 卡片有效曝光为 item 级，曝光同样传坑位 D 位（配镜流程坑位，与点击同坑位）。
    exposureSpmCnt: shoppingGuideSpmCnt,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const jumpTarget = useMemo(() => {
    const jumpData = item.jump_info || item;

    return {
      jump_type: jumpData.jump_type,
      Jump_target: jumpData.Jump_target ?? undefined,
      sku: jumpData.sku?.shop_sku ? { shop_sku: jumpData.sku.shop_sku } : undefined,
      product_group: jumpData.product_group,
    };
  }, [item]);

  const titleText = getPlainText(item.title);
  const accessibleLabel = titleText || getPlainText(item.des) || `Blog article ${index + 1}`;

  return (
    <li ref={cardRef} className="min-w-0">
      <MagicJump
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={accessibleLabel}
        className="group block h-full rounded-[12px]"
        {...shoppingGuideDomSpm}
      >
        <article className="flex h-full min-h-[363px] flex-col rounded-[12px] bg-white p-[24px]">
          <div className="flex h-[200px] w-full shrink-0 items-center justify-center" aria-hidden>
            {item.jump_image ? (
              <Image
                src={item.jump_image}
                alt={titleText}
                width={240}
                height={200}
                loading={moduleIndex < 3 ? 'eager' : 'lazy'}
                priority={moduleIndex < 3}
                className="block h-full w-full max-w-[240px] object-contain"
              />
            ) : null}
          </div>

          <div className="mt-[24px] flex min-h-[99px] flex-1 flex-col items-start">
            {item.title ? (
              <h4
                className="w-full font-bb text-[20px] leading-[24px]"
                style={{ color: item.title_color || DEFAULT_TITLE_COLOR }}
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
            ) : null}

            {item.des ? (
              <p
                className="mt-[12px] line-clamp-2 w-full font-br text-[14px] leading-[17px]"
                style={{ color: item.des_color || DEFAULT_SECONDARY_COLOR }}
                dangerouslySetInnerHTML={{ __html: item.des }}
              />
            ) : null}

            {item.btn_text ? (
              <span
                className="mt-auto inline-flex items-center pt-[12px] font-br text-[14px] leading-[17px]"
                style={{ color: item.btn_word_color || DEFAULT_SECONDARY_COLOR }}
              >
                {item.btn_text}
                <span className="ml-[4px]" aria-hidden>
                  &gt;
                </span>
              </span>
            ) : null}
          </div>
        </article>
      </MagicJump>
    </li>
  );
}

export default function BlogArticle({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BlogArticleProps) {
  const articles = (indexData.jump_target || []).filter(hasVisibleContent);
  const showHeading = indexData.layout_title_status === 1 && Boolean(indexData.layout_title);
  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_pc');
  // Data SPM DOM 打标（C 位模块码，请求头 data-spm=1 开关）：与楼层曝光上报的模块编码同值。
  const moduleDomSpm = useSpmDomAttr(spmModule);

  if (articles.length === 0) return null;

  return (
    <section
      className={`w-full py-[60px] ${showHeading ? 'min-h-[562px]' : ''}`}
      style={{ backgroundColor: indexData.bg_color || DEFAULT_SECTION_BACKGROUND }}
      {...moduleDomSpm}
    >
      {showHeading ? (
        <header className="flex flex-col items-center gap-[8px] px-[24px] text-center">
          <h3
            className="w-full font-bb text-[32px] leading-[39px]"
            style={{ color: indexData.layout_title_color || DEFAULT_HEADING_COLOR }}
            dangerouslySetInnerHTML={{ __html: indexData.layout_title || '' }}
          />
          {indexData.layout_subtitle ? (
            <h4
              className="w-full font-br text-[20px] leading-[24px]"
              style={{
                color:
                  indexData.layout_subtitle_color?.trim() ||
                  indexData.layout_subtitl_color?.trim() ||
                  DEFAULT_HEADING_SUBTITLE_COLOR,
              }}
              dangerouslySetInnerHTML={{ __html: indexData.layout_subtitle }}
            />
          ) : null}
        </header>
      ) : null}

      <ul className={`grid grid-cols-4 gap-[12px] px-[48px] ${showHeading ? 'mt-[28px]' : ''}`}>
        {articles.map((item, index) => (
          <BlogArticleCard
            key={item.id || `${item.title || 'blog-article'}-${index}`}
            item={item}
            index={index}
            indexData={indexData}
            moduleIndex={moduleIndex}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </ul>
    </section>
  );
}
